import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tenantsAPI } from '../lib/api';
import {
  Building2,
  Users,
  FileText,
  Plus,
  Settings,
  BarChart3,
  Loader2,
  ChevronRight,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Shield,
  Activity,
  TrendingUp,
  Edit,
  Trash2,
  Eye,
  Save,
  X,
  PieChart,
  Calendar,
  Award,
  Clock,
  Target
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantStats, setTenantStats] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantAnalytics, setTenantAnalytics] = useState(null);
  const [allAnalytics, setAllAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Create/Edit modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    maxUsers: 100,
    maxAssessments: 1000
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!user?.isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchTenants();
    fetchAllAnalytics();
  }, [user, navigate]);

  const fetchTenants = async () => {
    try {
      const response = await tenantsAPI.list();
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAnalytics = async () => {
    try {
      const response = await tenantsAPI.getAllAnalytics();
      setAllAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics overview:', error);
    }
  };

  const fetchTenantDetails = async (tenantId) => {
    setSelectedTenant(tenants.find(t => t.id === tenantId));
    setAnalyticsLoading(true);
    try {
      const [statsRes, usersRes, analyticsRes] = await Promise.all([
        tenantsAPI.getStats(tenantId),
        tenantsAPI.getUsers(tenantId),
        tenantsAPI.getAnalytics(tenantId)
      ]);
      setTenantStats(statsRes.data);
      setTenantUsers(usersRes.data);
      setTenantAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch tenant details:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setActionError('');
    
    try {
      if (editingTenant) {
        await tenantsAPI.update(editingTenant.id, formData);
      } else {
        await tenantsAPI.create(formData);
      }
      setShowCreateModal(false);
      setEditingTenant(null);
      setFormData({ name: '', description: '', maxUsers: 100, maxAssessments: 1000 });
      fetchTenants();
      fetchAllAnalytics();
    } catch (error) {
      setActionError(error.response?.data?.detail || 'Failed to save tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to deactivate this tenant? Users will lose access.')) return;
    
    try {
      await tenantsAPI.delete(tenantId);
      fetchTenants();
      fetchAllAnalytics();
    } catch (error) {
      console.error('Failed to deactivate tenant:', error);
    }
  };

  const openEditModal = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      description: tenant.description || '',
      maxUsers: tenant.maxUsers,
      maxAssessments: tenant.maxAssessments
    });
    setShowCreateModal(true);
  };

  // Calculate totals
  const totalUsers = tenants.reduce((sum, t) => sum + (t.userCount || 0), 0);
  const totalAssessments = tenants.reduce((sum, t) => sum + (t.assessmentCount || 0), 0);
  const activeTenants = tenants.filter(t => t.isActive).length;

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="super-admin-dashboard" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-violet-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Super Admin</h1>
                  <p className="text-sm text-amber-400">Multi-Tenant Management & Analytics</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => { setEditingTenant(null); setFormData({ name: '', description: '', maxUsers: 100, maxAssessments: 1000 }); setShowCreateModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
              data-testid="create-tenant-btn"
            >
              <Plus className="w-4 h-4" />
              Create Tenant
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{tenants.length}</div>
                <div className="text-xs text-slate-400">Total Tenants</div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400">{activeTenants}</div>
                <div className="text-xs text-slate-400">Active Tenants</div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-400">{allAnalytics?.totals?.totalUsers || totalUsers}</div>
                <div className="text-xs text-slate-400">Total Users</div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400">{allAnalytics?.totals?.totalAssessments || totalAssessments}</div>
                <div className="text-xs text-slate-400">Total Assessments</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-400">{allAnalytics?.totals?.overallAvgScore || 0}%</div>
                <div className="text-xs text-slate-400">Avg Score</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => { setActiveTab('overview'); setSelectedTenant(null); }}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'overview'
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            All Tenants
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === 'analytics'
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Platform Analytics
          </button>
          {selectedTenant && (
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === 'details'
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {selectedTenant.name} Analytics
            </button>
          )}
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Tenant</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Users</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Assessments</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Avg Score</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {tenants.map((tenant) => {
                    const analytics = allAnalytics?.tenants?.find(t => t.tenantId === tenant.id);
                    return (
                      <tr key={tenant.id} className="hover:bg-slate-700/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-violet-400" />
                            </div>
                            <div>
                              <div className="font-medium text-white">{tenant.name}</div>
                              <div className="text-xs text-slate-400">{tenant.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {tenant.isActive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                              <XCircle className="w-3 h-3" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-white">{analytics?.users || tenant.userCount || 0}</div>
                          <div className="text-xs text-slate-400">/ {tenant.maxUsers} max</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-white">{analytics?.assessments || tenant.assessmentCount || 0}</div>
                          <div className="text-xs text-slate-400">{analytics?.completed || 0} completed</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`text-lg font-semibold ${
                            (analytics?.avgScore || 0) >= 75 ? 'text-green-400' :
                            (analytics?.avgScore || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {analytics?.avgScore || 0}%
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => { fetchTenantDetails(tenant.id); setActiveTab('details'); }}
                              className="p-2 text-slate-400 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-colors"
                              title="View Analytics"
                              data-testid={`view-tenant-${tenant.id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(tenant)}
                              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                              title="Edit"
                              data-testid={`edit-tenant-${tenant.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {tenant.slug !== 'aspiritech' && (
                              <button
                                onClick={() => handleDeactivateTenant(tenant.id)}
                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Deactivate"
                                data-testid={`deactivate-tenant-${tenant.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && allAnalytics && (
          <div className="space-y-6">
            {/* Tenant Comparison Chart */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-400" />
                Tenant Comparison
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={allAnalytics.tenants}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="tenantName" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Bar dataKey="users" name="Users" fill="#8b5cf6" />
                    <Bar dataKey="assessments" name="Assessments" fill="#06b6d4" />
                    <Bar dataKey="completed" name="Completed" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Score Distribution Across Tenants */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-400" />
                  Average Scores by Tenant
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={allAnalytics.tenants} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" domain={[0, 100]} stroke="#9ca3af" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="tenantName" type="category" stroke="#9ca3af" tick={{ fontSize: 12 }} width={100} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #374151', borderRadius: '8px' }}
                        formatter={(value) => [`${value}%`, 'Avg Score']}
                      />
                      <Bar dataKey="avgScore" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  User Distribution
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={allAnalytics.tenants}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="users"
                        nameKey="tenantName"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {allAnalytics.tenants.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #374151', borderRadius: '8px' }}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'details' && selectedTenant && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
            ) : tenantAnalytics ? (
              <>
                {/* Tenant Header */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-violet-500/20 rounded-xl flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-violet-400" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">{selectedTenant.name}</h2>
                        <p className="text-slate-400">{selectedTenant.description || 'No description'}</p>
                      </div>
                    </div>
                    {selectedTenant.isActive ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                        <CheckCircle className="w-4 h-4" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 text-sm rounded-full">
                        <XCircle className="w-4 h-4" />
                        Inactive
                      </span>
                    )}
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-white">{tenantAnalytics.summary.totalUsers}</div>
                      <div className="text-xs text-slate-400">Total Users</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-400">{tenantAnalytics.summary.activeUsers7d}</div>
                      <div className="text-xs text-slate-400">Active (7d)</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-white">{tenantAnalytics.summary.totalAssessments}</div>
                      <div className="text-xs text-slate-400">Assessments</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-400">{tenantAnalytics.summary.completedAssessments}</div>
                      <div className="text-xs text-slate-400">Completed</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-amber-400">{tenantAnalytics.summary.avgScore}%</div>
                      <div className="text-xs text-slate-400">Avg Score</div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-400">
                        {Math.round((tenantAnalytics.summary.completedAssessments / tenantAnalytics.summary.totalAssessments) * 100) || 0}%
                      </div>
                      <div className="text-xs text-slate-400">Completion Rate</div>
                    </div>
                  </div>
                </div>

                {/* Assessment Trend */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Assessment Activity (Last 30 Days)
                  </h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={tenantAnalytics.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#9ca3af" 
                          tick={{ fontSize: 10 }}
                          tickFormatter={(value) => value.slice(5)}
                        />
                        <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#fff' }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="created" 
                          name="Created"
                          stroke="#8b5cf6" 
                          strokeWidth={2}
                          dot={{ fill: '#8b5cf6', r: 3 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="completed" 
                          name="Completed"
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ fill: '#10b981', r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Assessment Types and Score Distribution */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-purple-400" />
                      Assessment Types
                    </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={Object.entries(tenantAnalytics.typeStats).map(([name, stats]) => ({
                              name,
                              value: stats.count,
                              avgScore: stats.avgScore
                            }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {Object.keys(tenantAnalytics.typeStats).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #374151', borderRadius: '8px' }}
                            formatter={(value, name, props) => [
                              `${value} assessments (Avg: ${props.payload.avgScore}%)`,
                              props.payload.name
                            ]}
                          />
                          <Legend />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-amber-400" />
                      Score Distribution
                    </h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={tenantAnalytics.scoreDistribution}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="range" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                          <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #374151', borderRadius: '8px' }}
                          />
                          <Bar dataKey="count" name="Assessments" radius={[4, 4, 0, 0]}>
                            {tenantAnalytics.scoreDistribution.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={
                                  entry.range === '76-100' ? '#10b981' :
                                  entry.range === '51-75' ? '#f59e0b' :
                                  entry.range === '26-50' ? '#f97316' : '#ef4444'
                                } 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-cyan-400" />
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {tenantAnalytics.recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            activity.status === 'Completed' ? 'bg-green-400' : 'bg-amber-400'
                          }`} />
                          <div>
                            <p className="text-white font-medium">{activity.name}</p>
                            <p className="text-xs text-slate-400">
                              {activity.type} • by {activity.userName}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${
                            activity.score >= 75 ? 'text-green-400' :
                            activity.score >= 50 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {activity.score}%
                          </p>
                          <p className="text-xs text-slate-400">{formatDate(activity.lastModified)}</p>
                        </div>
                      </div>
                    ))}
                    {tenantAnalytics.recentActivity.length === 0 && (
                      <p className="text-center text-slate-400 py-8">No recent activity</p>
                    )}
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-700">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-400" />
                      Tenant Users ({tenantUsers.length})
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase">User</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Role</th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase">Joined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {tenantUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-slate-700/30">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                                  {u.firstName?.[0] || u.email?.[0]?.toUpperCase()}
                                </div>
                                <span className="text-white">{u.firstName} {u.lastName}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-slate-300">{u.email}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {u.isSuperAdmin && (
                                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Super Admin</span>
                                )}
                                {u.isAdmin && !u.isSuperAdmin && (
                                  <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded">Admin</span>
                                )}
                                {!u.isAdmin && !u.isSuperAdmin && (
                                  <span className="px-2 py-0.5 bg-slate-600 text-slate-300 text-xs rounded">User</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-slate-400 text-sm">
                              {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center text-slate-400 py-12">Failed to load analytics</div>
            )}
          </div>
        )}
      </main>

      {/* Create/Edit Tenant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
              </h2>
              <button
                onClick={() => { setShowCreateModal(false); setEditingTenant(null); }}
                className="p-1 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateTenant} className="p-6 space-y-4">
              {actionError && (
                <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {actionError}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Tenant Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="Acme Security Corp"
                  data-testid="tenant-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  placeholder="Brief description of the organization"
                  data-testid="tenant-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Max Users</label>
                  <input
                    type="number"
                    value={formData.maxUsers}
                    onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) })}
                    min={1}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="tenant-max-users-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Max Assessments</label>
                  <input
                    type="number"
                    value={formData.maxAssessments}
                    onChange={(e) => setFormData({ ...formData, maxAssessments: parseInt(e.target.value) })}
                    min={1}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="tenant-max-assessments-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setEditingTenant(null); }}
                  className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
                  data-testid="save-tenant-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingTenant ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
